import { useState, useEffect } from 'react';
import { getSession, signIn, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import axios from 'axios';
import _ from 'lodash';
import { useQueryClient } from 'react-query';

import { useAuthStore } from '../store/authStore';
import { User } from '../types/auth';
import type { AuthState } from '../store/authStore';
import { api, setIntentionalLogout } from '../lib/api';
import { USER_ROLES, UserRoleType, isAdminRole } from '../lib/role';
import { useNavigationRedirect } from './useNavigationRedirect';

interface LoginData {
  email: string;
  password: string;
}

interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface ErrorResponse {
  response: {
    data: {
      message: string;
    };
  };
}

interface UseAuthReturn {
  login: (data: LoginData) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  validateResetToken: (token: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  logout: () => void;
}

export function useAuth(): UseAuthReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const setAuth = useAuthStore((state: AuthState) => state.login);
  const clearAuth = useAuthStore((state: AuthState) => state.logout);
  const user = useAuthStore((state: AuthState) => state.user);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { getFirstAccessibleRoute } = useNavigationRedirect();

  // Debounced login to prevent double submission
  const debouncedSignIn = _.debounce(
    async (credentials: LoginData) => {
      return signIn('credentials', {
        ...credentials,
        redirect: false,
      });
    },
    300,
    { leading: true, trailing: false },
  );

  const login = async ({ email, password }: LoginData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Normalize email for consistent handling
      const normalizedEmail = email.toLowerCase().trim();

      // Attempt sign in
      const result = await debouncedSignIn({
        email: normalizedEmail,
        password,
      });

      if (result?.error && result.error !== 'undefined') {
        if (
          result.error.includes('Authentication failed') ||
          result.error.includes('Invalid credentials')
        ) {
          throw new Error('Incorrect email or password. Please try again.');
        } else if (result.error.includes('User not found')) {
          throw new Error(
            'No account found with this email. Please check your email or create a new account.',
          );
        } else {
          throw new Error('Incorrect credentials. Please try again later.');
        }
      }

      // Wait for session to be ready with timeout
      let session = null;
      let attempts = 0;
      const maxAttempts = 5;
      const delayMs = 500;

      while (!session?.user && attempts < maxAttempts) {
        session = await getSession();

        if (session?.user && session.accessToken) {
          break;
        }

        if (attempts === maxAttempts - 1) {
          throw new Error('Session initialization timeout. Please try again.');
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
        attempts++;
      }

      if (!session?.user || !session.accessToken) {
        throw new Error('Unable to initialize session. Please try again.');
      }

      const accessToken = session.accessToken;

      // Set up authentication state
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      localStorage.setItem('accessToken', accessToken);

      // Set auth state
      const authData = {
        user: session.user,
        token: accessToken,
        success: true,
      };

      setAuth(authData);

      // Get redirect path based on user role and permissions
      const redirectPath = getFirstAccessibleRoute();

      // Handle potential redirect loop
      if (redirectPath === '/login' && session.user) {
        const fallbackPath = session.user.role.toLowerCase().includes('admin')
          ? '/admin/dashboard'
          : '/profile';

        // setIsLoading(false);
        await router.push(fallbackPath);
        return;
      }

      // Navigate to appropriate route
      // setIsLoading(false);
      await router.push(redirectPath);
    } catch (err) {
      console.error('[useAuth] Login error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize auth state from stored token
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    const initializeAuth = async () => {
      if (!mounted) return;

      try {
        // Check localStorage first
        const storedToken = localStorage.getItem('accessToken');

        // Set up initial API auth if token exists
        if (storedToken) {
          api.defaults.headers.common['Authorization'] =
            `Bearer ${storedToken}`;
        }

        // Get current session
        const session = await getSession();
        if (!mounted) return;

        if (session?.user && session.accessToken) {
          // Valid session exists
          api.defaults.headers.common['Authorization'] =
            `Bearer ${session.accessToken}`;

          // Update auth state
          setAuth({
            user: session.user,
            token: session.accessToken,
            success: true,
          });

          // Update stored token
          localStorage.setItem('accessToken', session.accessToken);

          // // If we're on login page and have valid session, redirect
          // if (window.location.pathname === '/login') {
          //   const redirectPath = getFirstAccessibleRoute();
          //   if (redirectPath !== '/login') {
          //     timeoutId = setTimeout(() => {
          //       router.replace(redirectPath);
          //     }, 100);
          //   }
          // }
        } else {
          // No valid session, clean up
          clearAuth();
          localStorage.removeItem('accessToken');
          delete api.defaults.headers.common['Authorization'];
        }
      } catch (error) {
        console.error('[useAuth] Initialization error:', error);
        if (mounted) {
          clearAuth();
          localStorage.removeItem('accessToken');
          delete api.defaults.headers.common['Authorization'];
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Call initialize immediately without debounce
    initializeAuth();

    return () => {
      mounted = false;
      // if (timeoutId) {
      //   clearTimeout(timeoutId);
      // }
    };
  }, [setAuth, clearAuth]);

  const signup = async (data: SignupData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Ensure email is consistently normalized to lowercase
      const normalizedData = {
        ...data,
        email: data.email.toLowerCase().trim(),
      };

      await api.post('/auth/signup', JSON.stringify(normalizedData));
    } catch (err) {
      setError((err as ErrorResponse).response.data.message || 'Signup failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Normalize email for consistent case-insensitive handling
      const normalizedEmail = email.toLowerCase().trim();

      await api.post(
        '/auth/forgot-password',
        JSON.stringify({ email: normalizedEmail, source: 'admin' }),
      );
    } catch (err) {
      setError('An error occurred. Please try again later.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await api.post(
        '/auth/reset-password',
        JSON.stringify({ token, newPassword }),
      );
    } catch (err) {
      setError(
        'This reset link has expired or is invalid. Please request a new one.',
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const validateResetToken = async (token: string): Promise<boolean> => {
    try {
      // The backend returns an object with a 'valid' property: { valid: boolean }
      const response = await api.get<{ valid: boolean }>(
        `/auth/validate-reset-token?token=${token}`,
      );
      return response.data.valid; // Extract the valid property from the response
    } catch (err) {
      console.error(
        'Token validation failed:',
        err instanceof Error ? err.message : err,
      );
      return false;
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      setError(null);

      setIntentionalLogout(true);

      // Clear stored tokens
      localStorage.removeItem('accessToken');
      delete api.defaults.headers.common['Authorization'];

      clearAuth();

      try {
        queryClient.invalidateQueries();
      } catch (e) {
        console.warn('[useAuth] Failed to clear query cache on logout:', e);
      }

      // Sign out from next-auth
      await signOut({
        redirect: false,
      });

      // Reset loading state before navigation
      setIsLoading(false);

      // Redirect to login
      await router.replace('/login');
    } catch (err) {
      console.error('Logout error:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as ErrorResponse).response?.data?.message || 'Logout failed';
      setError(errorMessage);
      setIsLoading(false);
      router.replace('/login');
    } finally {
      setIntentionalLogout(false);
    }
  };

  return {
    login,
    signup,
    forgotPassword,
    resetPassword,
    validateResetToken,
    isLoading,
    error,
    user,
    logout,
  };
}
